import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import {
  BETTER_VARIANT_OPTIONS,
  BOARD_QUESTIONS,
  VARIANT_VERDICT_QUESTION,
  type AnswerChoice,
  type BoardAnswer,
} from '@cf/shared'
import { toast } from 'sonner'
import { AppShell, PageBody, initialsOf } from '@/components/AppShell'
import {
  AxisHeading,
  BoardPanel,
  EvidenceProse,
  orderedWordings,
} from '@/components/EvidenceCard'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Choice } from '@/components/ui/choice'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { withVariantVerdict } from '@/lib/answers'
import { reviewApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { findBoardIndexById } from './boardLink'
import {
  flatten,
  isAnswered,
  useAnswers,
  useInvite,
  useReport,
  useSaveBoardAnswer,
} from './useReview'

/** How long a comment sits still before it is sent. */
const COMMENT_DEBOUNCE_MS = 700

export function VerifyPage() {
  const { token } = useParams({ from: '/r/$token/verify' })
  const search = useSearch({ from: '/r/$token/verify' })
  const navigate = useNavigate()
  const invite = useInvite(token)
  const report = useReport(token)
  const answers = useAnswers(token)
  const saveBoard = useSaveBoardAnswer(token)

  const boards = useMemo(() => flatten(report.data?.axes ?? []), [report.data])
  const linkedIndex = search.id ? findBoardIndexById(boards, search.id) : -1
  const requestedIndex = linkedIndex >= 0 ? linkedIndex : (search.i ?? 0)
  const index = Math.max(0, Math.min(boards.length - 1, requestedIndex))
  const current = boards[index]

  // Resume where the reviewer stopped, but never fight an explicit link.
  const restored = useRef(false)
  useEffect(() => {
    if (
      restored.current ||
      search.i !== undefined ||
      search.id !== undefined ||
      !invite.data ||
      !boards.length
    )
      return
    restored.current = true
    const cursor = invite.data.progress.cursor
    if (cursor > 0 && cursor < boards.length) {
      void navigate({ to: '/r/$token/verify', params: { token }, search: { i: cursor } })
    }
  }, [invite.data, boards.length, navigate, search.i, search.id, token])

  const goTo = (next: number) => {
    const bounded = Math.max(0, Math.min(boards.length - 1, next))
    void navigate({ to: '/r/$token/verify', params: { token }, search: { i: bounded } })
    void reviewApi.cursor(token, bounded).catch(() => undefined)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (report.isLoading || answers.isLoading || !invite.data) return <Loading />
  if (!current) {
    return (
      <AppShell>
        <PageBody>
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nic do weryfikacji</EmptyTitle>
              <EmptyDescription>
                Ten zbiór nie ma żadnej szachownicy. Napisz do nas, a sprawdzimy link.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </PageBody>
      </AppShell>
    )
  }

  const answer = answers.data?.boards[current.board.id] ?? {}
  const wordings = orderedWordings(current.board)
  const comparing = wordings.length > 1
  // With one wording the board's own verdict is the first question; with two
  // it is derived from the per-wording verdicts, so it is not asked twice.
  const questions = comparing
    ? BOARD_QUESTIONS.filter((question) => question.id !== 'accuracy')
    : BOARD_QUESTIONS
  const betterOptions = BETTER_VARIANT_OPTIONS.map((option) => ({
    ...option,
    label:
      option.value === 'a' || option.value === 'b'
        ? `wersja ${wordings.find((item) => item.variant === option.value)?.label ?? option.value.toUpperCase()}`
        : option.label,
  }))
  const answeredCount = Object.values(answers.data?.boards ?? {}).filter(isAnswered).length

  const patch = (change: Partial<BoardAnswer>) => {
    saveBoard.mutate(
      { boardId: current.board.id, answer: { ...answer, ...change } },
      {
        onError: (error) =>
          toast.error('Nie udało się zapisać odpowiedzi', {
            description: error instanceof Error ? error.message : undefined,
          }),
      },
    )
  }

  return (
    <AppShell
      subtitle={`${invite.data.dataset.label} · ${invite.data.dataset.revision}`}
      identity={{
        initials: initialsOf(invite.data.nick),
        name: invite.data.displayName ?? invite.data.nick,
        detail: invite.data.title,
      }}
      right={
        <Button size="sm" asChild>
          <Link to="/r/$token/report" params={{ token }}>
            Cały raport
          </Link>
        </Button>
      }
    >
      <div className="sticky top-14 z-20 border-b bg-card/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-[1460px] items-center gap-5 px-5 py-3 sm:px-7">
          <span className="text-xs whitespace-nowrap text-muted-foreground">
            Oś <span className="font-medium text-foreground">{current.axis.ordinal}</span>{' '}
            z {report.data?.axes.length} · dowód{' '}
            <span className="font-medium text-foreground">{current.board.ordinal}</span> z{' '}
            {current.axis.boards.length}
          </span>
          <div className="flex flex-1 items-center gap-1">
            {(report.data?.axes ?? []).map((axis) => {
              const done = axis.boards.filter((board) =>
                isAnswered(answers.data?.boards[board.id]),
              ).length
              const start = boards.findIndex((item) => item.axis.key === axis.key)
              return (
                <button
                  key={axis.key}
                  type="button"
                  onClick={() => goTo(start)}
                  title={`Oś ${axis.ordinal}: ${axis.title} (${done}/${axis.boards.length})`}
                  style={{ flex: `${axis.boards.length} 1 0` }}
                  className={cn(
                    'h-1.5 overflow-hidden rounded-full bg-secondary transition-all',
                    axis.key === current.axis.key && 'ring-2 ring-amber ring-offset-2',
                  )}
                >
                  <span
                    className="block h-full rounded-full bg-primary transition-all"
                    style={{ width: `${(done / axis.boards.length) * 100}%` }}
                  />
                </button>
              )
            })}
          </div>
          <span className="text-xs whitespace-nowrap text-muted-foreground">
            Ocenione <span className="font-medium text-foreground">{answeredCount}</span> /{' '}
            {boards.length}
          </span>
        </div>
      </div>

      <PageBody className="grid items-start gap-6 lg:grid-cols-[minmax(320px,520px)_1fr]">
        <div className="lg:sticky lg:top-32">
          <BoardPanel board={current.board} />
        </div>

        <div className="flex flex-col gap-4">
          <AxisHeading board={current.board} axis={current.axis} />

          {wordings.map((wording) => (
            <EvidenceProse
              key={wording.variant}
              board={current.board}
              wording={wording}
              label={comparing ? wording.label : undefined}
            >
              {/* With two wordings each carries its own verdict, so the
                  reviewer judges the text they just read rather than
                  remembering it while scrolling. */}
              {comparing ? (
                <Field>
                  <FieldLabel>{VARIANT_VERDICT_QUESTION.prompt}</FieldLabel>
                  <Choice
                    ariaLabel={`${VARIANT_VERDICT_QUESTION.prompt} (wersja ${wording.label})`}
                    options={VARIANT_VERDICT_QUESTION.options}
                    value={answer.variants?.[wording.variant] ?? null}
                    onChange={(value) =>
                      patch({
                        // Choice is untyped over its options; these are
                        // VARIANT_VERDICT_QUESTION's, so the narrowing holds.
                        variants: withVariantVerdict(
                          answer.variants,
                          wording.variant,
                          value as AnswerChoice | null,
                        ),
                      } as Partial<BoardAnswer>)
                    }
                  />
                </Field>
              ) : null}
            </EvidenceProse>
          ))}

          <Card>
            <CardContent>
              <FieldGroup>
                {comparing ? (
                  <Field>
                    <FieldLabel>Który opis jest lepszy?</FieldLabel>
                    <Choice
                      ariaLabel="Który opis jest lepszy?"
                      options={betterOptions}
                      value={answer.better ?? null}
                      onChange={(value) =>
                        patch({ better: value } as Partial<BoardAnswer>)
                      }
                    />
                  </Field>
                ) : null}
                {questions.map((question) => (
                  <Field key={question.id}>
                    <FieldLabel>{question.prompt}</FieldLabel>
                    <Choice
                      ariaLabel={question.prompt}
                      options={question.options}
                      value={answer[question.id] ?? null}
                      onChange={(value) =>
                        patch({ [question.id]: value } as Partial<BoardAnswer>)
                      }
                    />
                  </Field>
                ))}
                <Field>
                  <FieldLabel htmlFor="comment">Komentarz weryfikatora</FieldLabel>
                  <CommentBox
                    id="comment"
                    key={current.board.id}
                    value={answer.comment ?? ''}
                    onSave={(comment) => patch({ comment })}
                  />
                </Field>
              </FieldGroup>
            </CardContent>
          </Card>

          <nav className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
            >
              Wstecz
            </Button>
            <div className="flex-1" />
            <span className="text-xs text-muted-foreground">
              {isAnswered(answer) ? 'Zapisano automatycznie' : null}
            </span>
            {index < boards.length - 1 ? (
              <Button onClick={() => goTo(index + 1)}>Dalej</Button>
            ) : (
              <Button asChild>
                <Link to="/r/$token/summary" params={{ token }}>
                  Zakończ i wyślij
                </Link>
              </Button>
            )}
          </nav>
        </div>
      </PageBody>
    </AppShell>
  )
}

/**
 * Text saves on a pause, not on every keystroke, and once more when the field
 * loses focus, so a reviewer who types a sentence and closes the tab still has
 * it stored.
 *
 * The draft is seeded once and never re-synced from the server, so give this a
 * `key` that changes with the board: a refetch landing mid-sentence must not
 * overwrite what is being typed.
 */
function CommentBox({
  id,
  value,
  onSave,
}: {
  id?: string
  value: string
  onSave: (value: string) => void
}) {
  const [draft, setDraft] = useState(value)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), [])

  return (
    <Textarea
      id={id}
      rows={3}
      value={draft}
      onChange={(event) => {
        const next = event.target.value
        setDraft(next)
        if (timer.current) clearTimeout(timer.current)
        timer.current = setTimeout(() => onSave(next), COMMENT_DEBOUNCE_MS)
      }}
      onBlur={() => {
        if (timer.current) clearTimeout(timer.current)
        if (draft !== value) onSave(draft)
      }}
    />
  )
}

function Loading() {
  return (
    <AppShell>
      <PageBody className="grid items-start gap-6 lg:grid-cols-[minmax(320px,520px)_1fr]">
        <Skeleton className="aspect-square w-full rounded-xl" />
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-40 w-full rounded-xl" />
          <Skeleton className="h-56 w-full rounded-xl" />
        </div>
      </PageBody>
    </AppShell>
  )
}
